import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Divider,
  Space,
  Tooltip,
  Table,
  Timeline,
  Tag,
  Button,
  Modal,
  Spin
} from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import ENVIRONMENT from '../../../../environments';
import axios from 'axios';
import './CycleTimeChart.scss';

const { Title } = Typography;

const DATE_FORMAT = "YYYY/MM/DD";

// Modal component for cycle time details
const CycleTimeDetailModal = ({ visible, onClose, action, payload, metricLabel }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Map action to metric key and label
  const metricColumnMap = {
    get_model_turnaround_time_details: {
      key: 'turnaround_time',
      label: 'Model Turnaround Time',
    },
    get_model_customer_rejection_details: {
      key: 'customer_rejections',
      label: 'Number of Customer Rejections',
    },
    get_model_admin_rejection_details: {
      key: 'admin_rejections',
      label: 'Number of Admin Rejections',
    },
    get_model_customer_approval_time_details: {
      key: 'customer_approval_time',
      label: 'Customer Approval Time Since Submission',
    },
  };

  useEffect(() => {
    if (!visible || !action || !payload) return;
    setLoading(true);
    setError(null);
    setData(null);
    axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, { ...payload, action })
      .then((res) => {
        setData(res.data);
      })
      .catch((err) => {
        setError("Error fetching details");
      })
      .finally(() => setLoading(false));
  }, [visible, action, payload]);

  const metricInfo = metricColumnMap[action] || {};
  const columns = [
    {
      title: 'Product ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      render: (id) => (
        <a
          href={`admin_products/${id}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#1890ff' }}
        >
          {id}
        </a>
      ),
    },
    {
      title: 'Product Name',
      dataIndex: 'name',
      key: 'name',
      width: 350,
      ellipsis: true,
    },
    {
      title: 'Company Name',
      dataIndex: 'company_name',
      key: 'company_name',
      width: 200,
      ellipsis: true,
    },
  ];
  if (metricInfo.key && metricInfo.label) {
    columns.push({
      title: metricInfo.label,
      dataIndex: metricInfo.key,
      key: metricInfo.key,
      width: 150,
      render: (val) => val !== undefined ? val : '-',
    });
  }

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      title={metricLabel || "Details"}
      width="80%"
      bodyStyle={{ maxHeight: '80vh', overflow: 'auto' }}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <Spin />
        </div>
      ) : error ? (
        <div style={{ color: 'red' }}>{error}</div>
      ) : Array.isArray(data) && data.length > 0 ? (
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ y: '60vh' }}
        />
      ) : (
        <div>No data available.</div>
      )}
    </Modal>
  );
};

const CycleTimeChart = ({ 
  chartStyle, 
  companyList, 
  setIsLoading, 
  masterDateRange = null, 
  masterPresetRange = null,
  masterCompanyFilter = null
}) => {
  const [cycleTimeData, setCycleTimeData] = useState([
    {
      key: '1',
      metric: 'Average Model Turnaround Time',
      value: '-',
    },
    {
      key: '2',
      metric: 'Average Number of Customer Rejections per Model',
      value: '-',
    },
    {
      key: '3',
      metric: 'Average Number of Admin Rejections per Model',
      value: '-',
    },
    {
      key: '4',
      metric: 'Average Customer Approval Time Since Submission',
      value: '-',
    }
  ]);
  const [cycleTimeLoading, setCycleTimeLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [modalPayload, setModalPayload] = useState(null);
  const [modalMetricLabel, setModalMetricLabel] = useState("");

  const fetchCycleTimeData = useCallback(async () => {
    try {
      setCycleTimeLoading(true);
      let startDate, endDate;
      
      // Always use master date range
      startDate = moment(masterDateRange?.start).format("YYYY-MM-DD");
      endDate = moment(masterDateRange?.end).format("YYYY-MM-DD");

      // Prepare payloads for different metrics
      const turnaroundRange = {
        start: startDate,
        end: endDate,
      };
      const customerRejRange = {
        start: startDate,
        end: endDate,
      };
      const adminRejRange = {
        start: startDate,
        end: endDate,
      };
      // For the new metric, use a fixed date range as per your example
      const customerApprovalRange = {
        start: startDate,
        end: endDate    
      };
      

      // Add company filter if selected
      const basePayload = {};
      if (masterCompanyFilter && masterCompanyFilter !== "ALL") {
        basePayload.company_id = masterCompanyFilter;
      }

      // Prepare API calls for different metrics
      const apiCalls = [
        // Average Model Turnaround Time
        axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, {
          action: "get_average_model_turnaround_time",
          start_date: turnaroundRange.start,
          end_date: turnaroundRange.end,
          ...basePayload
        }),
        // Average Customer Rejections per Model
        axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, {
          action: "get_average_customer_rejections_per_model",
          start_date: customerRejRange.start,
          end_date: customerRejRange.end,
          ...basePayload
        }),
        // Average Admin Rejections per Model
        axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, {
          action: "get_average_admin_rejections_per_model",
          start_date: adminRejRange.start,
          end_date: adminRejRange.end,
          ...basePayload
        }),
        // Average Customer Approval Time Since Submission
        axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, {
          action: "get_average_model_customer_approval_time",
          start_date: customerApprovalRange.start,
          end_date: customerApprovalRange.end,
          ...basePayload
        })
      ];

      const responses = await Promise.all(apiCalls);

      console.log('responses:', responses);
      
      setCycleTimeData([
        {
          key: '1',
          metric: 'Average Model Turnaround Time',
          value: responses[0].data?.average_turnaround_days == null
            ? '-'
            : `${responses[0].data.average_turnaround_days} days`,
        },
        {
          key: '2',
          metric: 'Average Number of Customer Rejections per Model',
          value: responses[1].data?.average_customer_rejections_per_model == null
            ? '-'
            : responses[1].data.average_customer_rejections_per_model.toString(),
        },
        {
          key: '3',
          metric: 'Average Number of Admin Rejections per Model',
          value: responses[2].data?.average_admin_rejections_per_model == null
            ? '-'
            : responses[2].data.average_admin_rejections_per_model.toString(),
        },
        {
          key: '4',
          metric: 'Average Customer Approval Time Since Submission',
          value: responses[3].data?.average_model_customer_approval_time == null
            ? '-'
            : `${responses[3].data.average_model_customer_approval_time} days`,
        }
      ]);

    } catch (error) {
      console.error("Error fetching cycle time data:", error);
      setCycleTimeData([
        {
          key: '1',
          metric: 'Average Model Turnaround Time',
          value: 'Error',
        },
        {
          key: '2',
          metric: 'Average Number of Customer Rejections per Model',
          value: 'Error',
        },
        {
          key: '3',
          metric: 'Average Number of Admin Rejections per Model',
          value: 'Error',
        },
        {
          key: '4',
          metric: 'Average Customer Approval Time Since Submission',
          value: 'Error',
        }
      ]);
    } finally {
      setCycleTimeLoading(false);
    }
  }, [masterDateRange, masterCompanyFilter, setIsLoading]);

  useEffect(() => {
    fetchCycleTimeData();
  }, [fetchCycleTimeData]);

  const cycleTimeColumns = [
    {
      title: 'Metric',
      dataIndex: 'metric',
      key: 'metric',
      render: (text) => {
        const tooltips = {
          'Average Model Turnaround Time': 'The average time in days from when a model is uploaded to the platform until the model is sent to the customer, regardless if the customer approves/rejects the submission. This can include multiple rounds of internal QA.',
          'Average Number of Customer Rejections per Model': 'The average count of a model is rejected by the customer before the model is approved.',
          'Average Number of Admin Rejections per Model': 'The average count a model is rejected by QA before the model is approved and sent to the customer for review.',
          'Average Customer Approval Time Since Submission': 'The average time in days from when model is upload to the platform until model is approved by the customer',
        };
        
        return (
          <span>
            {text}{' '}
            <Tooltip title={tooltips[text]} placement="topLeft">
              <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
            </Tooltip>
          </span>
        );
      },
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (value, record) => {
        const metricToAction = {
          'Average Model Turnaround Time': 'get_model_turnaround_time_details',
          'Average Number of Customer Rejections per Model': 'get_model_customer_rejection_details',
          'Average Number of Admin Rejections per Model': 'get_model_admin_rejection_details',
          'Average Customer Approval Time Since Submission': 'get_model_customer_approval_time_details',
        };
        const action = metricToAction[record.metric];
        const handleClick = () => {
          let start = moment(masterDateRange?.start, DATE_FORMAT).format('YYYY-MM-DD');
          let end = moment(masterDateRange?.end, DATE_FORMAT).format('YYYY-MM-DD');
          const payload = { start_date: start, end_date: end };
          if (masterCompanyFilter && masterCompanyFilter !== 'ALL') {
            payload.company_id = masterCompanyFilter;
          }
          setModalAction(action);
          setModalPayload(payload);
          setModalMetricLabel(record.metric);
          setModalOpen(true);
        };
        return (
          <a
            style={{
              cursor: value === '-' || value === 'Error' ? 'default' : 'pointer',
              color: value === '-' || value === 'Error' ? '#999' : '#1890ff',
              textDecoration: 'none'
            }}
            onClick={e => {
              e.preventDefault();
              if (value !== '-' && value !== 'Error') handleClick();
            }}
          >
            {value}
          </a>
        );
      }
    },
  ];

  // Helper function to parse numeric values from cycle time data
  const parseTimeValue = (value) => {
    if (value === '-' || value === 'Error') return null;
    const numericMatch = value.toString().match(/(\d+\.?\d*)/);
    return numericMatch ? parseFloat(numericMatch[1]) : null;
  };

  return (
    <Col span={24}>
      <Card className="cycleTimeCard" style={chartStyle}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={4} style={{ margin: 0 }}>Cycle Time Averages</Title>
          </Col>
        </Row>

        <Table
           columns={cycleTimeColumns}
           dataSource={cycleTimeData}
           pagination={false}
           size="middle"
           loading={cycleTimeLoading}
        />

        <Divider style={{ margin: "24px 0" }} />

        <div className="cycleTimeCard">
          <div className="cycleTimeCardHeader">
            <div className="cycleTimeCardHeaderTitle">
              Workflow Process Timeline
            </div>
          </div>

          <div className="timelineContainer">
            {(() => {
              // Parse values
              const turnaroundValue = parseTimeValue(cycleTimeData[0]?.value); // Average Model Turnaround Time
              const customerApprovalValue = parseTimeValue(cycleTimeData[3]?.value); // Average Customer Approval Time Since Submission
              const adminRejectionsValue = parseTimeValue(cycleTimeData[2]?.value); // Admin Rejections count
              const customerRejectionsValue = parseTimeValue(cycleTimeData[1]?.value); // Customer Rejections count
              
              // Check if we have the essential data for the timeline
              const hasCustomerApprovalData = customerApprovalValue !== null && customerApprovalValue > 0;
              const hasTurnaroundData = turnaroundValue !== null && turnaroundValue > 0;
              
              // If either essential metric is missing, show message instead of broken timeline
              if (!hasCustomerApprovalData || !hasTurnaroundData) {
                return (
                  <div className="timelineUnavailable">
                    <div className="timelineUnavailableTitle">
                      📊 Timeline data is not available
                    </div>
                    <div className="timelineUnavailableDesc">
                      Please select a different date range or check back later when data becomes available.
                    </div>
                  </div>
                );
              }
              
              // Calculate the customer review/rejection time (total - turnaround)
              const customerReviewTime = hasCustomerApprovalData && hasTurnaroundData ? 
                Math.max(0, parseFloat((customerApprovalValue - turnaroundValue).toFixed(2))) : 0;
              
              // Create timeline segments based on actual workflow
              const workflowSteps = [
                { 
                  label: 'Model Turnaround Time', 
                  value: cycleTimeData[0]?.value || 'N/A', 
                  numericValue: turnaroundValue || 0,
                  metric: cycleTimeData[0]?.metric, 
                  color: '#1890ff',
                  description: 'Internal development & QA'
                },
                { 
                  label: 'Customer Feedback Iterations', 
                  value: customerReviewTime > 0 ? `${customerReviewTime} days` : '0 days',
                  numericValue: customerReviewTime,
                  metric: 'Customer Review Time', 
                  color: '#fa8c16',
                  description: 'Customer feedback & fixes'
                }
              ];

              // Calculate proportional widths based on time values
              const totalTime = customerApprovalValue || turnaroundValue || 1; // Fallback to prevent division by zero
              const hasValidData = hasCustomerApprovalData || hasTurnaroundData;
              
              return (
                <div>
                  {/* Timeline Header */}
                  <div className={`timelineHeader ${hasCustomerApprovalData ? 'timelineHeaderApproval' : 'timelineHeaderDevelopment'}`}>
                    <div className="timelineHeaderTitle">
                      {hasCustomerApprovalData ? 'Total Customer Approval Timeline' : 'Model Development Timeline'}
                    </div>
                    <div className="timelineHeaderValue">
                      {hasCustomerApprovalData ? 
                        (cycleTimeData[3]?.value || 'N/A') : 
                        (cycleTimeData[0]?.value || 'N/A')
                      }
                    </div>
                    <div className="timelineHeaderDesc">
                      {hasCustomerApprovalData ? 
                        'From model upload to customer approval' : 
                        'Internal development and QA time'
                      }
                    </div>
                  </div>

                  {/* Main Timeline Bar */}
                  <div className="timelineBar">
                    {workflowSteps.map((step, index) => {
                      // Handle cases where we might only have partial data
                      let rawPercentage;
                      if (hasCustomerApprovalData && step.numericValue > 0) {
                        rawPercentage = (step.numericValue / totalTime) * 100;
                      } else if (!hasCustomerApprovalData && hasTurnaroundData && step.numericValue > 0) {
                        // If only turnaround data exists, show it as 100%
                        rawPercentage = step.label === 'Model Turnaround Time' ? 100 : 0;
                      } else {
                        rawPercentage = 0;
                      }
                      
                      const percentage = Math.max(rawPercentage, rawPercentage > 0 ? 8 : 0); // Minimum 8% for visibility, but 0 if no data
                      
                      // Only show if there's actual time allocated or it's the only available data
                      if (!hasValidData || (step.numericValue <= 0 && rawPercentage === 0)) {
                        return null;
                      }
                      
                      return (
                        <div
                          key={index}
                          className="timelineStep"
                          style={{
                            background: `linear-gradient(135deg, ${step.color}, ${step.color}dd)`,
                            width: `${percentage}%`,
                            borderRight: index < workflowSteps.length - 1 ? '2px solid white' : 'none',
                          }}
                          title={`${step.label}: ${step.value} (${step.description})`}
                        >
                          {/* Top Section: Step Number and Label */}
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            {/* Step Number */}
                            <div
                              className="timelineStepNumber"
                              style={{
                                width: percentage < 12 ? '20px' : '28px',
                                height: percentage < 12 ? '20px' : '28px',
                                color: step.color,
                                fontSize: percentage < 12 ? '10px' : '13px',
                              }}
                            >
                              {index + 1}
                            </div>
                            {/* Step Label */}
                            {percentage > 15 && (
                              <div
                                className="timelineStepLabel"
                                style={{ fontSize: percentage < 25 ? '9px' : '10px' }}
                              >
                                {percentage < 25 ? step.label.replace(' Time', '') : step.label}
                              </div>
                            )}
                          </div>
                          {/* Center Section: Value */}
                          <div
                            className="timelineStepValue"
                            style={{ fontSize: percentage < 12 ? '11px' : percentage < 20 ? '13px' : '15px', padding: percentage < 12 ? '3px 6px' : '4px 8px' }}
                          >
                            {step.value}
                          </div>
                          {/* Bottom Section: Description */}
                          {percentage > 20 && (
                            <div className="timelineStepDesc">
                              {step.description}
                            </div>
                          )}
                          {/* Percentage */}
                          {rawPercentage > 8 && percentage > 18 && (
                            <div className="timelineStepPercent">
                              {Math.round(rawPercentage)}%
                            </div>
                          )}
                        </div>
                      );
                    }).filter(Boolean)}
                  </div>

                  {/* Rejection Metrics Below */}
                  <div className="rejectionMetrics">
                    {/* Admin Rejections */}
                    <div className="rejectionBox rejectionBoxAdmin">
                      <div className="rejectionBoxIconAdmin">
                        QA
                      </div>
                      <div className="rejectionBoxTitle">
                        Admin Rejections
                      </div>
                      <div className="rejectionBoxValueAdmin">
                        {cycleTimeData[2]?.value || 'N/A'}
                      </div>
                      <div className="rejectionBoxDesc">
                        per model average
                      </div>
                    </div>
                    {/* Customer Rejections */}
                    <div className="rejectionBox rejectionBoxCustomer">
                      <div className="rejectionBoxIconCustomer">
                        👤
                      </div>
                      <div className="rejectionBoxTitle">
                        Customer Rejections
                      </div>
                      <div className="rejectionBoxValueCustomer">
                        {cycleTimeData[1]?.value || 'N/A'}
                      </div>
                      <div className="rejectionBoxDesc">
                        per model average
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Legend */}
          <div className="legend">
            {[
              { color: '#1890ff', label: 'Model Development & QA Time' },
              { color: '#fa8c16', label: 'Customer Review Period' },
              { color: '#52c41a', label: 'Admin QA Rejections' },
              { color: '#fa8c16', label: 'Customer Rejections' }
            ].map((item, index) => (
              <div key={index} className="legendItem">
                <div className="legendColor" style={{ background: item.color }} />
                <span className="legendLabel">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
        <CycleTimeDetailModal
          visible={modalOpen}
          onClose={() => setModalOpen(false)}
          action={modalAction}
          payload={modalPayload}
          metricLabel={modalMetricLabel}
        />
      </Card>
    </Col>
  );
};

export default CycleTimeChart;
